/**
 * The frame clock, and the reason it is not just `requestAnimationFrame`.
 *
 * A browser stops calling `requestAnimationFrame` in a hidden tab. In a
 * single-player game that is correct and desirable. In a **lockstep** game it is
 * a fault: a client that stops stepping stops submitting, its peer waits at the
 * tick barrier for input that is never coming, and the match freezes for the
 * person who is still looking at it. Construction halts mid-building, nothing
 * moves, and a minute and a half later the relay drops the silent player and
 * both sides disconnect.
 *
 * That is the exact sequence a player reported, and it happens the moment you
 * put two browser windows side by side and click on one of them.
 *
 * So when the page is hidden, a **worker** drives the ticks instead. Timers in
 * a worker keep running when the page's own do not, and the worker is built
 * from a blob rather than a file — this project ships no assets, and a clock is
 * not a good reason to start.
 */

/** Milliseconds per driven frame while hidden. Matches the simulation rate. */
const HIDDEN_INTERVAL_MS = 33

const WORKER_SOURCE = `
let timer = null
self.onmessage = (e) => {
  if (e.data === 'start' && timer === null) {
    timer = setInterval(() => self.postMessage(0), ${HIDDEN_INTERVAL_MS})
  } else if (e.data === 'stop' && timer !== null) {
    clearInterval(timer)
    timer = null
  }
}
`

export interface Clock {
  stop(): void
  /** True while the worker is driving, rather than the animation frame. */
  readonly background: boolean
}

/**
 * Drive `onFrame` at roughly display rate while visible, and at the simulation
 * rate while hidden. `dtMs` is clamped: a tab that was hidden for a minute must
 * not hand the simulation a sixty-second step.
 */
export function startClock(onFrame: (dtMs: number, visible: boolean) => void): Clock {
  let raf = 0
  let last = 0
  let running = true
  let worker: Worker | null = null
  let workerUrl = ''
  let background = false

  const step = (visible: boolean): void => {
    const stamp = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const dt = last === 0 ? 16.7 : Math.min(stamp - last, 100)
    last = stamp
    onFrame(dt, visible)
  }

  const loop = (): void => {
    if (!running || background) return
    step(true)
    raf = requestAnimationFrame(loop)
  }

  const startWorker = (): void => {
    if (worker || typeof Worker === 'undefined' || typeof Blob === 'undefined') return
    try {
      workerUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }))
      worker = new Worker(workerUrl)
      worker.onmessage = () => {
        if (running && background) step(false)
      }
      worker.postMessage('start')
    } catch {
      // No worker available. The tab will simply pause while hidden, which is
      // the old behaviour rather than a new failure.
      worker = null
    }
  }

  const stopWorker = (): void => {
    worker?.postMessage('stop')
    worker?.terminate()
    worker = null
    if (workerUrl) URL.revokeObjectURL(workerUrl)
    workerUrl = ''
  }

  const onVisibility = (): void => {
    const hidden = typeof document !== 'undefined' && document.hidden
    if (hidden === background) return
    background = hidden
    if (hidden) {
      cancelAnimationFrame(raf)
      startWorker()
    } else {
      stopWorker()
      last = 0
      raf = requestAnimationFrame(loop)
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility)
  }
  raf = requestAnimationFrame(loop)

  return {
    get background() {
      return background
    },
    stop() {
      running = false
      cancelAnimationFrame(raf)
      stopWorker()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
    },
  }
}
